import { MapPin } from "lucide-react";
import { Basemap } from "components/basemap/basemap";
import { useIdentity } from "contexts/identity-context";
import * as QueryKeys from "data";
import {
  fetchBuildingsByAddressId,
  fetchLandParcelsCadastres,
  fetchLandParcelsProperties,
} from "data/queries-common";
import { AddressSearch } from "pages/geoscape-data/explorer/address-search";
import { ZoomControl } from "pages/geoscape-data/explorer/zoom-control";
import { useCallback, useEffect, useRef, useState } from "react";
import {
  LayerProps,
  Layer,
  MapRef,
  Marker,
  MarkerEvent,
  Source,
} from "react-map-gl/maplibre";
import { useQuery } from "@tanstack/react-query";
import { resolveIdentityId } from "utils/identity";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Skeleton } from "@/components/ui/skeleton";
import { Spinner } from "@/components/ui/spinner";
import { InsightsPanel } from "./insights-panel";
import { OctagonAlert } from "lucide-react";

export const PropertyInsights = () => {
  const mapRef = useRef<MapRef>();
  const [coordinates, setCoordinates] = useState({
    latitude: -35.32186,
    longitude: 149.14683,
  });
  const initialZoom: number = 7;
  const addressZoom: number = 17;

  const [dynamicZoom, setDynamicZoom] = useState(initialZoom);
  const [showMarker, setShowMarker] = useState(false);
  const [showInsights, setShowInsights] = useState(false);
  const [address, setAddress] = useState("");
  const [addressId, setAddressId] = useState("");
  const [identityState] = useIdentity();

  const identityId = resolveIdentityId(identityState);

  const landParcelsLayer: LayerProps = {
    id: "source-land-parcels",
    type: "fill",
    paint: {
      "fill-color": "#00ffff",
      "fill-opacity": 0.5,
    },
  };

  const buildingLayer: LayerProps = {
    id: "source-buildings",
    type: "fill",
    paint: {
      "fill-color": "#0011ff",
      "fill-opacity": 0.5,
    },
  };

  const resetCompass = () => {
    if (mapRef.current === undefined) return;
    mapRef.current.easeTo({
      bearing: 0,
      pitch: 0,
    });
  };
  const handleZoom = (zoom: number): void => {
    setDynamicZoom(zoom);
    mapRef.current?.flyTo({ zoom: zoom, duration: 500 });
  };

  const handleClearAddress = (): void => {
    setShowMarker(false);
    setShowInsights(false);
  };
  const onMarkerClick = (e: MarkerEvent): void => {
    setShowInsights(true);
  };

  const buildingsQuery = useQuery({
    queryKey: [QueryKeys.buildingsKey, identityId, addressId],
    queryFn: () => fetchBuildingsByAddressId(addressId),
    enabled: !!addressId,
  });

  const landParcelsPropertiesQuery = useQuery({
    queryKey: [QueryKeys.landParcelsPropertiesKey, identityId, addressId],
    queryFn: () => fetchLandParcelsProperties(addressId),
    enabled: !!addressId,
  });

  const landParcelsCadastresQuery = useQuery({
    queryKey: [QueryKeys.landParcelsCadastresKey, identityId, addressId],
    queryFn: () => fetchLandParcelsCadastres(addressId),
    enabled: !!addressId,
  });

  const onSelectAddress = useCallback(
    ({
      longitude,
      latitude,
      geocoder_attributes,
    }: {
      longitude: number;
      latitude: number;
      geocoder_attributes: { [key: string]: string };
    }) => {
      setCoordinates({
        latitude: latitude,
        longitude: longitude,
      });
      geocoder_attributes["longitude"] = longitude.toString();
      geocoder_attributes["latitude"] = latitude.toString();

      setAddress(geocoder_attributes["formatted_address"]);
      setAddressId(geocoder_attributes["address_identifier"]);

      mapRef.current?.flyTo({
        center: [longitude, latitude],
        duration: 2000,
        zoom: addressZoom,
        offset: [-200, 0],
      });
      setDynamicZoom(addressZoom);
      setShowMarker(true);
      setShowInsights(true);
    },
    [],
  );

  useEffect(() => {
    if (mapRef.current) {
      mapRef.current.resize();
    }
  }, [showInsights]);

  return (
    <div className="flex flex-row">
      <Basemap
        // @ts-ignore
        mapRef={mapRef}
        latitude={coordinates.latitude}
        longitude={coordinates.longitude}
        zoom={initialZoom}
        onZoom={(zoom: any) => setDynamicZoom(zoom)}
        style={{
          width: showInsights ? "50%" : "100%",
          height: "100%",
          position: "absolute",
          left: 0,
          top: 0,
          display: "flex",
        }}
        basemap="Imagery"
      >
        {showMarker && (
          <Marker
            key={"selected-address"}
            longitude={coordinates.longitude}
            latitude={coordinates.latitude}
            // @ts-ignore
            onClick={onMarkerClick}
            style={{
              position: "absolute",
              left: 0,
              top: 0,
            }}
          >
            <MapPin className="size-8 text-destructive" fill="currentColor" />
            {landParcelsPropertiesQuery.isEnabled &&
              landParcelsPropertiesQuery.isPending && (
                <Spinner className="size-6" />
              )}
          </Marker>
        )}
        <div className="absolute right-8 top-20 flex flex-col justify-end gap-4 group-has-data-[collapsible=icon]/sidebar-wrapper:top-16">
          <AddressSearch
            onSelectAddress={onSelectAddress}
            onClearAddress={handleClearAddress}
          />
          <ZoomControl
            resetCompass={resetCompass}
            dynamicZoom={dynamicZoom}
            onZoom={handleZoom}
          />
        </div>
        {landParcelsPropertiesQuery.isSuccess && buildingsQuery.isSuccess && (
          <>
            <Source
              id="source-land-parcels"
              type="geojson"
              data={
                landParcelsPropertiesQuery.data as GeoJSON.FeatureCollection
              }
            >
              <Layer {...landParcelsLayer} />
            </Source>

            <Source
              id="source-buildings"
              type="geojson"
              data={
                {
                  type: "FeatureCollection",
                  features: buildingsQuery.data.data.map(
                    (data: any, index: number) => ({
                      type: "Feature",
                      properties: {},
                      geometry: data.footprint2d,
                    }),
                  ),
                } as GeoJSON.FeatureCollection
              }
            >
              <Layer {...buildingLayer} />
            </Source>
          </>
        )}
      </Basemap>
      {showInsights && (
        <div className="absolute right-0 top-16 flex h-[calc(100%-4rem)] w-1/2 group-has-data-[collapsible=icon]/sidebar-wrapper:top-12 group-has-data-[collapsible=icon]/sidebar-wrapper:h-[calc(100%-3rem)]">
          <div className="w-full overflow-scroll">
            {landParcelsPropertiesQuery.isSuccess &&
              landParcelsCadastresQuery.isSuccess &&
              buildingsQuery.isSuccess && (
                <InsightsPanel
                  address={address}
                  addressId={addressId}
                  open={showInsights}
                  propertyData={landParcelsPropertiesQuery.data}
                  cadastreData={landParcelsCadastresQuery.data}
                  buildingData={buildingsQuery.data}
                />
              )}
            {((landParcelsPropertiesQuery.isEnabled &&
              landParcelsPropertiesQuery.isPending) ||
              (landParcelsCadastresQuery.isEnabled &&
                landParcelsCadastresQuery.isPending)) && (
              <div className="flex flex-col gap-4 p-4">
                <Skeleton className="h-72.5 w-full" />
                <Skeleton className="h-65 w-full" />
                <Skeleton className="h-56 w-full" />
                <Skeleton className="h-67.5 w-full" />
              </div>
            )}
            {(landParcelsPropertiesQuery.isError ||
              landParcelsCadastresQuery.isError) && (
              <Alert variant="destructive" className="m-4">
                <AlertDescription>Unable to load data</AlertDescription>
              </Alert>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
